/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MAGNESIUM CHLORIDE USP CALCULATOR
 * ═══════════════════════════════════════════════════════════════════════════
 * Version: 1.0.0
 * Updated: March 2026
 * Compatible with: gwb-calculators.js v5.2+
 *
 * DISPATCHER REGISTRY ENTRY — add to gwb-calculators.js:
 * 'mg-chloride': {
 *     name: 'Magnesium Chloride Calculator',
 *     initFnName: 'renderMgChlorideCalculator',
 *     file: 'mg-chloride-calculator.js',
 *     available: true
 * }
 *
 * TABS:
 *   1. Oral Solution   — daily ml of prepared solution per desired Mg target
 *   2. Direct Powder   — grams / tsp to stir into juice or food
 *   3. Magnesium Oil   — DIY recipe by concentration ratio & batch size
 *   4. Baths & Soaks   — full bath, foot soak, sitz bath by strength
 *   5. Conversions     — grams ↔ tsp ↔ tbsp ↔ cups + elemental Mg
 * ═══════════════════════════════════════════════════════════════════════════
 */

(function () {
  'use strict';

  window.renderMgChlorideCalculator = function (container) {

    // ═══════════════════════════════════════════════════════════════
    // CONSTANTS
    // ═══════════════════════════════════════════════════════════════

    // MgCl₂·6H₂O powder bulk density / volume equivalents
    const GRAMS_PER_TSP  = 6.6;
    const GRAMS_PER_TBSP = GRAMS_PER_TSP * 3;    // 19.8 g
    const GRAMS_PER_CUP  = GRAMS_PER_TBSP * 16;  // 316.8 g

    // Elemental Mg fraction: Mg MW 24.305 / MgCl2·6H2O MW 203.31 = 11.96%
    const MG_FRACTION = 24.305 / 203.31;

    // Standard oral prep: 33 g per 1 L -> ~3.947 mg elemental Mg per ml
    const ORAL_GRAMS_PER_LITER = 33;
    const MG_PER_ML_ORAL = ORAL_GRAMS_PER_LITER * MG_FRACTION;

    // Volume conversions
    const ML_PER_OZ   = 29.5735;
    const ML_PER_TSP  = 5;
    const ML_PER_TBSP = 15;

    // Magnesium oil: ~0.13 ml per spray (standard pump)
    const ML_PER_SPRAY = 0.13;

    // Bulk density for powder volume -> weight (oil calculator)
    const POWDER_BULK_DENSITY = 1.3; // g/ml

    // Bath & soak amounts (grams) by type and strength
    const BATH_TABLE = {
      'full-bath': { mild: 50,  medium: 100, strong: 150 },
      'foot-soak': { mild: 25,  medium: 50,  strong: 75  },
      'sitz-bath': { mild: 30,  medium: 60,  strong: 90  }
    };

    const BATH_INSTRUCTIONS = {
      'full-bath': 'Fill tub with warm water, add magnesium chloride, stir briefly, and soak for 20-30 minutes.',
      'foot-soak': 'Fill a basin with warm water, dissolve magnesium chloride fully, and soak feet for 20-30 minutes.',
      'sitz-bath': 'Fill sitz bath basin with warm water, add magnesium chloride, and soak for 15-20 minutes.'
    };

    // ═══════════════════════════════════════════════════════════════
    // UTILITIES
    // ═══════════════════════════════════════════════════════════════

    function nf(n, d) {
      if (d === undefined) d = (n % 1 === 0) ? 0 : 1;
      return parseFloat(n.toFixed(d)).toLocaleString();
    }

    function $ (sel) { return container.querySelector(sel); }

    function setError(id, msg) {
      const el = container.querySelector('#' + id);
      if (el) el.textContent = msg;
    }

    function showResult(id, html) {
      const el = container.querySelector('#' + id);
      if (!el) return;
      el.innerHTML = html;
      el.classList.add('show');
      if (window.innerWidth < 768) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    function hideResult(id) {
      const el = container.querySelector('#' + id);
      if (el) { el.innerHTML = ''; el.classList.remove('show'); }
    }

    // ═══════════════════════════════════════════════════════════════
    // RENDER HTML
    // ═══════════════════════════════════════════════════════════════

    container.innerHTML = `
      <div class="calculator-section">

        <!-- TABS -->
        <div class="calc-tabs" role="tablist">
          <button class="calc-tab active" data-tab="oral"   role="tab" aria-selected="true">💧 Oral Solution</button>
          <button class="calc-tab"        data-tab="direct" role="tab" aria-selected="false">🥤 Direct Powder</button>
          <button class="calc-tab"        data-tab="oil"    role="tab" aria-selected="false">🧴 Magnesium Oil</button>
          <button class="calc-tab"        data-tab="bath"   role="tab" aria-selected="false">🛁 Baths &amp; Soaks</button>
          <button class="calc-tab"        data-tab="conv"   role="tab" aria-selected="false">⚖️ Conversions</button>
        </div>

        <!-- ─── TAB 1: ORAL SOLUTION ─── -->
        <div class="calc-section active" data-content="oral">
          <h3>Daily Oral Magnesium Dose</h3>
          <p class="calc-hint">Standard prep: 33 g MgCl₂ in 1 liter of water provides ~3.95 mg elemental magnesium per ml. Enter your daily target to find out how much solution to take.</p>

          <div class="calc-presets">
            <button class="preset-btn" data-oral-mg="90">90 mg</button>
            <button class="preset-btn" data-oral-mg="180">180 mg</button>
            <button class="preset-btn" data-oral-mg="270">270 mg</button>
            <button class="preset-btn active" data-oral-mg="360">360 mg</button>
            <button class="preset-btn" data-oral-mg="420">420 mg</button>
          </div>

          <div class="calc-row">
            <div>
              <label for="oral-mg">Desired elemental magnesium (mg/day)</label>
              <input type="number" id="oral-mg" value="360" min="1" max="1000" placeholder="e.g., 360">
            </div>
          </div>

          <div class="calc-error" id="oral-error"></div>

          <div class="calc-buttons">
            <button class="calc-btn" id="oral-calculate">Calculate Dose</button>
            <button class="calc-btn-secondary" id="oral-clear">Clear</button>
          </div>

          <div class="calc-result" id="oral-result"></div>
        </div>

        <!-- ─── TAB 2: DIRECT POWDER ─── -->
        <div class="calc-section" data-content="direct">
          <h3>Add Powder Directly to Juice or Food</h3>
          <p class="calc-hint">No pre-mixing needed — stir directly into any non-carbonated drink or food. Dissolves in seconds.</p>

          <div class="calc-presets">
            <button class="preset-btn" data-direct-mg="90">90 mg</button>
            <button class="preset-btn" data-direct-mg="180">180 mg</button>
            <button class="preset-btn" data-direct-mg="270">270 mg</button>
            <button class="preset-btn active" data-direct-mg="360">360 mg</button>
            <button class="preset-btn" data-direct-mg="420">420 mg</button>
          </div>

          <div class="calc-row">
            <div>
              <label for="direct-mg">Desired elemental magnesium (mg)</label>
              <input type="number" id="direct-mg" value="360" min="1" max="500" placeholder="e.g., 360">
            </div>
          </div>

          <div class="calc-error" id="direct-error"></div>

          <div class="calc-buttons">
            <button class="calc-btn" id="direct-calculate">Calculate Amount</button>
            <button class="calc-btn-secondary" id="direct-clear">Clear</button>
          </div>

          <div class="calc-result" id="direct-result"></div>
        </div>

        <!-- ─── TAB 3: MAGNESIUM OIL ─── -->
        <div class="calc-section" data-content="oil">
          <h3>DIY Magnesium Oil Recipe</h3>
          <p class="calc-hint">Mix Magnesium Chloride USP powder with distilled water to make your own topical spray. 1:1 by volume is standard; higher ratios give a more concentrated result.</p>

          <div class="calc-row">
            <div>
              <label for="oil-ratio">Concentration (Powder : Water)</label>
              <select id="oil-ratio">
                <option value="1">1:1 — Standard Strength</option>
                <option value="2" selected>2:1 — Double Strength (recommended)</option>
                <option value="3">3:1 — Triple Strength</option>
              </select>
            </div>
            <div>
              <label for="oil-batch">Batch Size</label>
              <select id="oil-batch">
                <option value="4">4 oz (small bottle)</option>
                <option value="8" selected>8 oz (standard spray bottle)</option>
                <option value="16">16 oz (large bottle)</option>
                <option value="32">32 oz (quart)</option>
                <option value="custom">Custom...</option>
              </select>
            </div>
          </div>

          <div class="calc-row" id="oil-custom-row" style="display:none;">
            <div>
              <label for="oil-custom-ml">Custom batch volume (ml)</label>
              <input type="number" id="oil-custom-ml" placeholder="e.g., 500" min="1">
            </div>
          </div>

          <div class="calc-error" id="oil-error"></div>

          <div class="calc-buttons">
            <button class="calc-btn" id="oil-calculate">Calculate Recipe</button>
            <button class="calc-btn-secondary" id="oil-clear">Clear</button>
          </div>

          <div class="calc-result" id="oil-result"></div>
        </div>

        <!-- ─── TAB 4: BATHS & SOAKS ─── -->
        <div class="calc-section" data-content="bath">
          <h3>Bath &amp; Foot Soak Calculator</h3>
          <p class="calc-hint">Get the right amount for a relaxing magnesium soak. Pair with Epsom Salt for a layered experience.</p>

          <div class="calc-row">
            <div>
              <label for="bath-type">Type of Soak</label>
              <select id="bath-type">
                <option value="full-bath">Full Bath</option>
                <option value="foot-soak">Foot Soak</option>
                <option value="sitz-bath">Sitz Bath</option>
              </select>
            </div>
            <div>
              <label for="bath-strength">Strength</label>
              <select id="bath-strength">
                <option value="mild">Mild — Beginner-friendly</option>
                <option value="medium" selected>Medium — Standard</option>
                <option value="strong">Strong — Intensive</option>
              </select>
            </div>
          </div>

          <div class="calc-error" id="bath-error"></div>

          <div class="calc-buttons">
            <button class="calc-btn" id="bath-calculate">Calculate Amount</button>
            <button class="calc-btn-secondary" id="bath-clear">Clear</button>
          </div>

          <div class="calc-result" id="bath-result"></div>
        </div>

        <!-- ─── TAB 5: CONVERSIONS ─── -->
        <div class="calc-section" data-content="conv">
          <h3>Measurement Converter</h3>
          <p class="calc-hint">Convert between grams, teaspoons, tablespoons, and cups for Magnesium Chloride Hexahydrate powder. Also shows elemental magnesium content of the converted amount.</p>

          <div class="calc-row">
            <div>
              <label for="conv-amount">Amount</label>
              <input type="number" id="conv-amount" placeholder="e.g., 33" min="0.1" step="0.1">
            </div>
            <div>
              <label for="conv-from">From</label>
              <select id="conv-from">
                <option value="grams" selected>Grams (g)</option>
                <option value="tsp">Teaspoons (tsp)</option>
                <option value="tbsp">Tablespoons (tbsp)</option>
                <option value="cup">Cups</option>
              </select>
            </div>
            <div>
              <label for="conv-to">To</label>
              <select id="conv-to">
                <option value="tsp" selected>Teaspoons (tsp)</option>
                <option value="grams">Grams (g)</option>
                <option value="tbsp">Tablespoons (tbsp)</option>
                <option value="cup">Cups</option>
              </select>
            </div>
          </div>

          <div class="calc-error" id="conv-error"></div>

          <div class="calc-buttons">
            <button class="calc-btn" id="conv-calculate">Convert</button>
            <button class="calc-btn-secondary" id="conv-clear">Clear</button>
          </div>

          <div class="calc-result" id="conv-result"></div>
        </div>

      </div>
    `;

    // ═══════════════════════════════════════════════════════════════
    // TAB SWITCHING
    // ═══════════════════════════════════════════════════════════════

    container.querySelectorAll('.calc-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        container.querySelectorAll('.calc-tab').forEach(t => {
          t.classList.remove('active');
          t.setAttribute('aria-selected', 'false');
        });
        container.querySelectorAll('.calc-section').forEach(s => s.classList.remove('active'));
        tab.classList.add('active');
        tab.setAttribute('aria-selected', 'true');
        const panel = container.querySelector(`.calc-section[data-content="${tab.dataset.tab}"]`);
        if (panel) panel.classList.add('active');
      });
    });

    // ═══════════════════════════════════════════════════════════════
    // TAB 1 — ORAL SOLUTION
    // ═══════════════════════════════════════════════════════════════

    function calcOral() {
      const desiredMg = parseFloat($('#oral-mg').value);

      if (!desiredMg || desiredMg <= 0) {
        setError('oral-error', 'Please enter a valid magnesium target in mg.');
        hideResult('oral-result');
        return;
      }
      setError('oral-error', '');

      const mlPerDay   = desiredMg / MG_PER_ML_ORAL;
      const ozPerDay   = mlPerDay  / ML_PER_OZ;
      const tspPerDay  = mlPerDay  / ML_PER_TSP;
      const tbspPerDay = mlPerDay  / ML_PER_TBSP;
      const daysPerL   = 1000 / mlPerDay;

      showResult('oral-result', `
        <h4>Daily Dose — ${nf(desiredMg, 0)} mg Elemental Magnesium</h4>
        <div><strong>Solution to take:</strong> ${nf(mlPerDay, 1)} ml per day</div>
        <div style="margin-top:4px;color:#6B4921;">${nf(ozPerDay, 2)} fl oz &nbsp;/&nbsp; ${nf(tspPerDay, 2)} tsp &nbsp;/&nbsp; ${nf(tbspPerDay, 2)} tbsp</div>
        <div style="margin-top:8px;"><strong>1 liter of solution lasts:</strong> approximately ${nf(daysPerL, 0)} days at this rate</div>
        <div class="calc-tip">
          <strong>📋 Prep Reminder:</strong>
          Dissolve <strong>33 g (approx. 5 level tsp)</strong> of Magnesium Chloride USP in 1 liter of water in a glass container.
          Stir until completely clear. Store at room temperature, away from direct sunlight.
          Use glass, ceramic, or food-grade plastic only — avoid metal containers.
        </div>
      `);
    }

    function clearOral() {
      $('#oral-mg').value = '';
      setError('oral-error', '');
      hideResult('oral-result');
      container.querySelectorAll('[data-oral-mg]').forEach(b => b.classList.remove('active'));
    }

    $('#oral-calculate').addEventListener('click', calcOral);
    $('#oral-clear').addEventListener('click', clearOral);

    container.querySelectorAll('[data-oral-mg]').forEach(btn => {
      btn.addEventListener('click', () => {
        container.querySelectorAll('[data-oral-mg]').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        $('#oral-mg').value = btn.dataset.oralMg;
        calcOral();
      });
    });

    // ═══════════════════════════════════════════════════════════════
    // TAB 2 — DIRECT POWDER
    // ═══════════════════════════════════════════════════════════════

    function calcDirect() {
      const desiredMg = parseFloat($('#direct-mg').value);

      if (!desiredMg || desiredMg <= 0) {
        setError('direct-error', 'Please enter a valid magnesium target in mg.');
        hideResult('direct-result');
        return;
      }
      setError('direct-error', '');

      const grams = desiredMg / (MG_FRACTION * 1000);
      const tsp   = grams / GRAMS_PER_TSP;
      const tbsp  = grams / GRAMS_PER_TBSP;

      showResult('direct-result', `
        <h4>Direct Powder — ${nf(desiredMg, 0)} mg Elemental Magnesium</h4>
        <div><strong>Powder needed:</strong> ${nf(grams, 2)} g</div>
        <div style="margin-top:4px;color:#6B4921;">${nf(tsp, 2)} tsp &nbsp;/&nbsp; ${nf(tbsp, 3)} tbsp</div>
        <div class="calc-tip">
          <strong>💡 Taste Tip:</strong>
          Orange juice or lemonade masks the mild bitter-salty flavor best.
          Smoothies, yogurt, and applesauce also work well.
          Start with half the amount if you're new to magnesium supplementation and increase gradually.
        </div>
      `);
    }

    function clearDirect() {
      $('#direct-mg').value = '';
      setError('direct-error', '');
      hideResult('direct-result');
      container.querySelectorAll('[data-direct-mg]').forEach(b => b.classList.remove('active'));
    }

    $('#direct-calculate').addEventListener('click', calcDirect);
    $('#direct-clear').addEventListener('click', clearDirect);

    container.querySelectorAll('[data-direct-mg]').forEach(btn => {
      btn.addEventListener('click', () => {
        container.querySelectorAll('[data-direct-mg]').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        $('#direct-mg').value = btn.dataset.directMg;
        calcDirect();
      });
    });

    // ═══════════════════════════════════════════════════════════════
    // TAB 3 — MAGNESIUM OIL
    // ═══════════════════════════════════════════════════════════════

    $('#oil-batch').addEventListener('change', () => {
      $('#oil-custom-row').style.display = $('#oil-batch').value === 'custom' ? '' : 'none';
    });

    function calcOil() {
      const ratio = parseFloat($('#oil-ratio').value);
      let totalMl;

      if ($('#oil-batch').value === 'custom') {
        totalMl = parseFloat($('#oil-custom-ml').value);
        if (!totalMl || totalMl <= 0) {
          setError('oil-error', 'Please enter a valid custom volume in ml.');
          hideResult('oil-result');
          return;
        }
      } else {
        totalMl = parseFloat($('#oil-batch').value) * ML_PER_OZ;
      }

      setError('oil-error', '');

      const totalParts     = ratio + 1;
      const waterMl        = totalMl / totalParts;
      const powderVolumeMl = (totalMl * ratio) / totalParts;
      const powderGrams    = powderVolumeMl * POWDER_BULK_DENSITY;
      const powderTsp      = powderGrams / GRAMS_PER_TSP;
      const powderTbsp     = powderGrams / GRAMS_PER_TBSP;
      const waterOz        = waterMl / ML_PER_OZ;
      const totalOz        = totalMl / ML_PER_OZ;
      const totalMgBatch   = powderGrams * MG_FRACTION * 1000;
      const mgPerSpray     = totalMgBatch / (totalMl / ML_PER_SPRAY);
      const ratioLabel     = ratio === 1 ? 'Standard (1:1)' : ratio === 2 ? 'Double (2:1)' : 'Triple (3:1)';

      showResult('oil-result', `
        <h4>Magnesium Oil Recipe — ${nf(totalOz, 0)} oz Batch (${ratioLabel})</h4>
        <div><strong>Powder:</strong> ${nf(powderGrams, 1)} g &nbsp;=&nbsp; ${nf(powderTsp, 1)} tsp &nbsp;/&nbsp; ${nf(powderTbsp, 2)} tbsp</div>
        <div style="margin-top:4px;"><strong>Water:</strong> ${nf(waterMl, 0)} ml &nbsp;=&nbsp; ${nf(waterOz, 1)} oz distilled or RO water</div>
        <div style="margin-top:4px;"><strong>Approx. per spray:</strong> ${nf(mgPerSpray, 1)} mg elemental magnesium</div>
        <div class="calc-tip">
          <strong>📋 Instructions:</strong>
          <ol style="margin:6px 0 0 18px;line-height:1.8;font-size:.95rem;">
            <li>Warm ${nf(waterMl, 0)} ml of distilled water (not boiling)</li>
            <li>Add ${nf(powderGrams, 1)} g of Magnesium Chloride USP powder</li>
            <li>Stir until completely dissolved and allow to cool fully</li>
            <li>Transfer to a <strong>glass spray bottle</strong></li>
            <li>Apply to arms, legs, or feet and massage into skin</li>
          </ol>
          For sensitive skin, apply over a thin layer of lotion or dilute with a little more water.
        </div>
      `);
    }

    function clearOil() {
      $('#oil-ratio').value = '2';
      $('#oil-batch').value = '8';
      $('#oil-custom-row').style.display = 'none';
      if ($('#oil-custom-ml')) $('#oil-custom-ml').value = '';
      setError('oil-error', '');
      hideResult('oil-result');
    }

    $('#oil-calculate').addEventListener('click', calcOil);
    $('#oil-clear').addEventListener('click', clearOil);

    ['#oil-ratio', '#oil-batch'].forEach(sel => {
      $(sel).addEventListener('change', () => {
        if ($('#oil-batch').value !== 'custom') calcOil();
      });
    });

    // ═══════════════════════════════════════════════════════════════
    // TAB 4 — BATHS & SOAKS
    // ═══════════════════════════════════════════════════════════════

    function calcBath() {
      const type     = $('#bath-type').value;
      const strength = $('#bath-strength').value;
      const grams    = BATH_TABLE[type] && BATH_TABLE[type][strength];

      if (!grams) {
        setError('bath-error', 'Unable to calculate — please check your selections.');
        hideResult('bath-result');
        return;
      }
      setError('bath-error', '');

      const tsp  = grams / GRAMS_PER_TSP;
      const tbsp = grams / GRAMS_PER_TBSP;
      const cups = grams / GRAMS_PER_CUP;

      const typeLabel     = type === 'full-bath' ? 'Full Bath' : type === 'foot-soak' ? 'Foot Soak' : 'Sitz Bath';
      const strengthLabel = strength.charAt(0).toUpperCase() + strength.slice(1);

      showResult('bath-result', `
        <h4>${typeLabel} — ${strengthLabel} Strength</h4>
        <div><strong>Amount:</strong> ${nf(grams, 0)} g of Magnesium Chloride USP</div>
        <div style="margin-top:4px;color:#6B4921;">${nf(tsp, 1)} tsp &nbsp;/&nbsp; ${nf(tbsp, 1)} tbsp &nbsp;/&nbsp; ${nf(cups, 2)} cups</div>
        <div style="margin-top:8px;">${BATH_INSTRUCTIONS[type]}</div>
        <div class="calc-tip">
          <strong>💡 Tip:</strong> For an enhanced relaxation soak, combine with
          <a href="/products/epsom-salt">Epsom Salt (Magnesium Sulfate)</a>.
          Start with Mild strength if you have sensitive skin and increase over time.
        </div>
      `);
    }

    function clearBath() {
      $('#bath-type').value = 'full-bath';
      $('#bath-strength').value = 'medium';
      setError('bath-error', '');
      hideResult('bath-result');
    }

    $('#bath-calculate').addEventListener('click', calcBath);
    $('#bath-clear').addEventListener('click', clearBath);

    ['#bath-type', '#bath-strength'].forEach(sel => {
      $(sel).addEventListener('change', calcBath);
    });

    // ═══════════════════════════════════════════════════════════════
    // TAB 5 — CONVERSIONS
    // ═══════════════════════════════════════════════════════════════

    const UNIT_LABELS = {
      grams: 'grams (g)',
      tsp:   'teaspoons (tsp)',
      tbsp:  'tablespoons (tbsp)',
      cup:   'cups'
    };

    function toGrams(amount, unit) {
      switch (unit) {
        case 'grams': return amount;
        case 'tsp':   return amount * GRAMS_PER_TSP;
        case 'tbsp':  return amount * GRAMS_PER_TBSP;
        case 'cup':   return amount * GRAMS_PER_CUP;
        default:      return NaN;
      }
    }

    function fromGrams(grams, unit) {
      switch (unit) {
        case 'grams': return grams;
        case 'tsp':   return grams / GRAMS_PER_TSP;
        case 'tbsp':  return grams / GRAMS_PER_TBSP;
        case 'cup':   return grams / GRAMS_PER_CUP;
        default:      return NaN;
      }
    }

    function calcConv() {
      const amount = parseFloat($('#conv-amount').value);

      if (!amount || amount <= 0) {
        setError('conv-error', 'Please enter a valid amount to convert.');
        hideResult('conv-result');
        return;
      }

      const from = $('#conv-from').value;
      const to   = $('#conv-to').value;

      if (from === to) {
        setError('conv-error', 'Please select different units for conversion.');
        hideResult('conv-result');
        return;
      }

      setError('conv-error', '');

      const grams     = toGrams(amount, from);
      const converted = fromGrams(grams, to);
      const mg        = grams * MG_FRACTION * 1000;

      if (isNaN(converted)) {
        setError('conv-error', 'Conversion failed. Please check your selections.');
        hideResult('conv-result');
        return;
      }

      showResult('conv-result', `
        <h4>Conversion Result</h4>
        <div><strong>${nf(amount, 2)} ${UNIT_LABELS[from]}</strong> = <strong>${nf(converted, 3)} ${UNIT_LABELS[to]}</strong></div>
        <div style="margin-top:8px;"><strong>Powder weight:</strong> ${nf(grams, 2)} g</div>
        <div><strong>Elemental magnesium:</strong> approximately ${nf(mg, 0)} mg</div>
        <div class="calc-tip">
          <strong>Reference:</strong> 1 level tsp ≈ 6.6 g &nbsp;/&nbsp; 1 tbsp ≈ 19.8 g &nbsp;/&nbsp; 1 cup ≈ 316.8 g
          <br>For precise dosing, a kitchen scale is more accurate than volume measurement.
        </div>
      `);
    }

    function clearConv() {
      $('#conv-amount').value = '';
      $('#conv-from').value = 'grams';
      $('#conv-to').value = 'tsp';
      setError('conv-error', '');
      hideResult('conv-result');
    }

    $('#conv-calculate').addEventListener('click', calcConv);
    $('#conv-clear').addEventListener('click', clearConv);

    // ═══════════════════════════════════════════════════════════════
    // KEYBOARD SUPPORT (Enter = calculate, Delete on empty = clear)
    // ═══════════════════════════════════════════════════════════════

    container.querySelectorAll('input[type="number"]').forEach(input => {
      input.addEventListener('keydown', e => {
        const section = input.closest('.calc-section');
        if (!section) return;
        if (e.key === 'Enter') {
          const btn = section.querySelector('.calc-btn');
          if (btn) btn.click();
        }
        if (e.key === 'Delete' && !input.value) {
          const btn = section.querySelector('.calc-btn-secondary');
          if (btn) btn.click();
        }
      });
    });

  };

})();